import { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shadcn/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@shadcn/components/ui/dialog';
import { Button } from '@shadcn/components/ui/button';
import { Input } from '@shadcn/components/ui/input';
import { Label } from '@shadcn/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shadcn/components/ui/select';
import superagent from 'superagent';
import { toast } from 'react-toastify';
import { backendUrl } from '@helpers/consts';

export default function GrowthRateModal({ isOpen, onClose, onDataRefresh }) {
  const [activeTab, setActiveTab] = useState('AU');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [growthRates, setGrowthRates] = useState({
    AU: {},
    US: {},
    UK: {}
  });

  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  
  useEffect(() => {
    const initializeEmptyRates = (region) => {
      const rates = {};
      rates[selectedYear] = {};
      monthNames.forEach((_, index) => {
        rates[selectedYear][index + 1] = 0;
      });
      return rates;
    };

    setGrowthRates({
      AU: initializeEmptyRates('AU'),
      US: initializeEmptyRates('US'),
      UK: initializeEmptyRates('UK'),
    });
  }, []);

  
  useEffect(() => {
    if (isOpen) {
      fetchGrowthRates();
    }
  }, [isOpen]);

  const fetchGrowthRates = async () => {
    setLoading(true);
    try {
      const regions = ['AU', 'US', 'UK'];
      const newRates = { AU: {}, US: {}, UK: {} };
      const availableYears = new Set();

      for (const region of regions) {
        const response = await superagent
          .get(`${backendUrl}/growth-rates/byRegion`)
          .query({ region })
          .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
          .set('Accept', 'application/json');

        if (response.body) {
          newRates[region] = {};
          
          response.body.forEach(yearData => {
            availableYears.add(yearData.year);
            newRates[region][yearData.year] = yearData.months;
          });
        }
      }

      
      const yearsList = Array.from(availableYears).sort((a, b) => a - b);
      if (yearsList.length === 0) {
        const currentYear = new Date().getFullYear();
        yearsList.push(currentYear, currentYear + 1);
      }
      setYears(yearsList);
      
      
      if (yearsList.length > 0) {
        setSelectedYear(yearsList[0]);
      }

      setGrowthRates(newRates);
    } catch (err) {
      console.error('Error fetching growth rates:', err);
      toast.error('Failed to load growth rates');
    } finally {
      setLoading(false);
    }
  };

  const handleRateChange = (region, month, value) => {
    // Allow empty string or valid numbers including negative
    const numValue = value === '' || value === '-' ? value : (parseFloat(value) || 0);
    setGrowthRates(prevRates => {
      const newRates = { ...prevRates };
      if (!newRates[region][selectedYear]) {
        newRates[region][selectedYear] = {};
      }
      newRates[region][selectedYear][month] = numValue;
      return newRates;
    });
  };

  // Make sure rates are converted to numbers before submission
  const prepareRatesForSubmission = (regionRates) => {
    const rates = [];
    if (regionRates) {
      Object.entries(regionRates).forEach(([month, rate]) => {
        // Convert any string value to number
        const numRate = typeof rate === 'string' ? (rate === '' || rate === '-' ? 0 : parseFloat(rate)) : rate;
        rates.push({
          month: parseInt(month),
          year: parseInt(selectedYear),
          rate: numRate
        });
      });
    }
    return rates;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const regions = ['AU', 'US', 'UK'];
      let successCount = 0;
      
      for (const region of regions) {
        if (growthRates[region][selectedYear]) {
          const rates = prepareRatesForSubmission(growthRates[region][selectedYear]);
          
          if (rates.length > 0) {
            await superagent
              .post(`${backendUrl}/growth-rates/batchUpdate`)
              .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
              .set('Accept', 'application/json')
              .send({
                region: region,
                rates
              });
            
            successCount++;
          }
        }
      }
      

    

              
        try {
          await superagent
            .post(`${backendUrl}/demand/manualUpdateForecast`)
            .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
            .set('Accept', 'application/json');
        } catch (err) {
        
          console.log('error', err);
        } 

        if (successCount === 3) {
          toast.success('All region growth rates updated successfully');
        } else if (successCount > 0) {
          toast.success(`Growth rates updated for ${successCount} regions`);
        } else {
          toast.info('No growth rates were updated');
        }

        if (onDataRefresh) {
          await onDataRefresh();
        }
        
        onClose();
        
    } catch (err) {
      console.error('Error updating growth rates:', err);
      toast.error('Failed to update growth rates: ' + (err.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  const isMonthInPast = (month, year) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; 
    
    return (year < currentYear) || (year === currentYear && month < currentMonth);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto md:max-w-[800px] p-4 sm:p-6">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="text-xl font-bold">Forecast Growth Rate</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between bg-gray-50 p-4 rounded-lg">
            <div className="w-full">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <div className="w-full sm:w-auto mb-4 sm:mb-0">
                  <Select 
                    value={selectedYear.toString()} 
                    onValueChange={(value) => setSelectedYear(parseInt(value))}
                  >
                    <SelectTrigger className="w-full sm:w-[180px]">
                      <SelectValue placeholder="Select Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map(year => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="py-10 flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <Tabs 
              defaultValue="AU" 
              value={activeTab} 
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList className="grid grid-cols-3 w-full mb-6">
                <TabsTrigger value="AU" className="text-sm sm:text-base">Australia</TabsTrigger>
                <TabsTrigger value="US" className="text-sm sm:text-base">United States</TabsTrigger>
                <TabsTrigger value="UK" className="text-sm sm:text-base">United Kingdom</TabsTrigger>
              </TabsList>

              {['AU', 'US', 'UK'].map(region => (
                <TabsContent key={region} value={region} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {monthNames.map((month, index) => {
                      const monthNum = index + 1;
                      const rateValue = growthRates[region][selectedYear]?.[monthNum] || 0;
                      const isPastMonth = isMonthInPast(monthNum, selectedYear);
                      
                      return (
                        <div 
                          key={`${region}-${month}`} 
                          className={`p-3 rounded-lg border ${isPastMonth ? 'bg-gray-50' : 'bg-white'}`}
                        >
                          <Label 
                            htmlFor={`${region}-${month}`}
                            className={`block mb-2 font-medium ${isPastMonth ? 'text-gray-500' : 'text-gray-700'}`}
                          >
                            {month} Growth Rate (%)
                            {isPastMonth && (
                              <span className="ml-2 text-xs text-gray-400">(Past)</span>
                            )}
                          </Label>
                          <div className="relative">
                            <Input
                              id={`${region}-${month}`}
                              type="text" 
                              value={rateValue}
                              onChange={(e) => handleRateChange(region, monthNum, e.target.value)}
                              onKeyDown={(e) => {
                                // Only allow: numbers, decimal point, negative sign, and control keys
                                if (
                                  !/^[-0-9.]$/.test(e.key) && 
                                  !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)
                                ) {
                                  e.preventDefault();
                                }
                                

                                if (e.key === '-' && e.target.selectionStart !== 0) {
                                  e.preventDefault();
                                }
                                

                                if (e.key === '.' && e.target.value.includes('.')) {
                                  e.preventDefault();
                                }
                              }}
                              onBlur={(e) => {
                                // When user leaves the field, format the value properly
                                if (e.target.value === '' || e.target.value === '-') {
                                  handleRateChange(region, monthNum, '0');
                                }
                              }}
                              className={`w-full pr-6 ${isPastMonth ? 'bg-gray-100 text-gray-500' : ''}`}
                              placeholder="0.00"
                              disabled={isPastMonth}
                            />
                            <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                              %
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100 text-sm text-blue-700">
                    <p>Growth rates represent the percentage change in sales forecast from month to month.</p>
                    <p className="mt-1">Negative values indicate expected decreases in sales.</p>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          )}
              
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} 
